<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import { getExample, parsePhoneNumber } from "awesome-phonenumber";
import CountryPicker from "@/components/CountryPicker.vue";
import { defaultCountryForLocale } from "@/composables/useCountries";
import { useLocale } from "@/composables/useLocale";

const props = defineProps<{
  submitting: boolean;
  initialE164?: string;
}>();

const emit = defineEmits<{ submit: [whatsapp: string] }>();

const { t } = useI18n();
const { locale } = useLocale();

// Seed country + national digits from an existing E.164 (e.g. when the user
// comes back from the OTP screen via "Use a different number"). Falls back
// to the locale-derived default country with an empty input.
function seedFromE164(e164: string | undefined): { country: string; raw: string } {
  if (e164) {
    const parsed = parsePhoneNumber(e164);
    if (parsed.valid && parsed.regionCode && parsed.number?.significant) {
      return { country: parsed.regionCode, raw: parsed.number.significant };
    }
  }
  return { country: defaultCountryForLocale(locale.value), raw: "" };
}

const seed = seedFromE164(props.initialE164);
const country = ref<string>(seed.country);
const raw = ref<string>(seed.raw);
const touched = ref(false);

// If the UI locale changes (e.g. ?lang=es applied after mount) reset the
// country to the locale default — but only while the user hasn't typed.
watch(locale, (next) => {
  if (raw.value.length === 0) {
    country.value = defaultCountryForLocale(next);
  }
});

const placeholder = computed(() => {
  try {
    const example = getExample(country.value, "mobile");
    return example.number?.national ?? t("whatsapp.placeholder");
  } catch {
    return t("whatsapp.placeholder");
  }
});

const e164 = computed<string | null>(() => {
  const digits = raw.value.replace(/[^\d]/g, "");
  if (!digits) return null;
  const parsed = parsePhoneNumber(digits, { regionCode: country.value });
  return parsed.valid ? (parsed.number?.e164 ?? null) : null;
});

const valid = computed(() => e164.value !== null);
const showError = computed(() => touched.value && raw.value.length > 0 && !valid.value);

function onSubmit(): void {
  touched.value = true;
  if (!valid.value || props.submitting || e164.value === null) return;
  emit("submit", e164.value);
}
</script>

<template>
  <form class="whatsapp-form gate-card" novalidate @submit.prevent="onSubmit">
    <div class="whatsapp-form__icon" aria-hidden="true">
      <svg viewBox="0 0 24 24" width="28" height="28" fill="none">
        <path
          d="M12 3 4.5 5.5v6c0 4.5 3 8 7.5 9.5 4.5-1.5 7.5-5 7.5-9.5v-6L12 3Z"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linejoin="round"
        />
        <path
          d="m9 12 2 2 4-4.5"
          stroke="currentColor"
          stroke-width="1.6"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </div>

    <h2 class="gate-heading whatsapp-form__heading">{{ t("whatsapp.heading") }}</h2>
    <p class="whatsapp-form__intro">{{ t("whatsapp.intro") }}</p>

    <label class="visually-hidden" for="whatsapp-input">{{ t("whatsapp.label") }}</label>
    <div class="whatsapp-form__input-row">
      <CountryPicker v-model="country" />
      <input
        id="whatsapp-input"
        v-model="raw"
        class="gate-input whatsapp-form__input"
        type="tel"
        inputmode="tel"
        autocomplete="tel-national"
        :placeholder="placeholder"
        :aria-invalid="showError"
        :aria-describedby="showError ? 'whatsapp-error' : 'whatsapp-hint'"
        @blur="touched = true"
      />
    </div>
    <p v-if="showError" id="whatsapp-error" class="gate-error-text" role="alert">
      {{ t("whatsapp.invalid") }}
    </p>
    <p v-else id="whatsapp-hint" class="whatsapp-form__hint">{{ t("whatsapp.hint") }}</p>

    <button
      type="submit"
      class="gate-button whatsapp-form__submit"
      :disabled="!valid || submitting"
    >
      <span v-if="submitting" class="gate-spinner" aria-hidden="true" />
      {{ submitting ? t("whatsapp.sending") : t("whatsapp.submit") }}
    </button>
  </form>
</template>

<style scoped>
.whatsapp-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  text-align: left;
}

.whatsapp-form__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: var(--radius);
  background-color: var(--color-primary-soft);
  color: var(--color-primary);
  margin-bottom: var(--space-2);
}

.whatsapp-form__heading {
  margin-bottom: var(--space-1);
}

.whatsapp-form__intro {
  font-size: var(--font-size-md);
  color: var(--color-text-secondary);
  line-height: 1.5;
  margin-bottom: var(--space-3);
}

.whatsapp-form__input-row {
  display: flex;
  gap: var(--space-2);
  align-items: stretch;
  position: relative;
}

.whatsapp-form__input {
  flex: 1;
  min-width: 0;
}

.whatsapp-form__hint {
  font-size: var(--font-size-sm);
  color: var(--color-text-muted);
}

.whatsapp-form__submit {
  margin-top: var(--space-3);
}
</style>
